import {
  ConnectedClassIndicator,
  deserify,
  theme,
} from '@geneo2-web/shared-ui';
import { useMediaQuery } from '@mui/material';
import {
  McqMultipleContentModel,
  McqSingleContentModel,
  Question,
  TfContentModel,
} from '@protos/content_management/content.db_pb';
import {
  getClickerDisconnectReason,
  isBaseStationEffectivelyOnline,
  isQuestionSessionMatched,
  useClickerAssessmentContext,
} from '../../../../app/Context/ClickerAssessmentContextProvider';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { calculateMarksMCQM } from '../../../../utils/functions';
import { convertAnswerOptionstoNumbers } from './ResponseList';

interface IProps {
  activeQuestion?: Question;
  showResponse?: boolean;
  /** True while start/end question-session API is in flight */
  isQuestionSessionLoading?: boolean;
}
export const ConnectedClassIndicatorWithDetails = (props: IProps) => {
  const { activeQuestion, showResponse, isQuestionSessionLoading } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    upsertConnectedClassQuestionSessionApiv2,
    unsubscribeQuestionFromFirestore,
    unsubscribeFromFirestore,
    questionSessionData,
    caBaseStationData,
    upsertConnectedClassSessionApiv2,
  } = useClickerAssessmentContext();

  const {
    assessment_content,
    current_question_index,
    clicker_assessment_session_data,
    teacher_assessment_session_details,
    ca_base_station_details,
    student_list,
    ca_question_session_details,
  } = deserify(useAppSelector((state) => state.clickerAssessment));

  const { current_assessmemt_classStats } = deserify(
    useAppSelector((state) => state.manageHomework)
  );

  const assessmentClassId = current_assessmemt_classStats?.assessment?.classId;
  const assessmentSectionId =
    current_assessmemt_classStats?.assessment?.sectionId;
  const expectedQuestionSessionId =
    ca_question_session_details?.question_session_id;

  const isBaseOnline = isBaseStationEffectivelyOnline(
    caBaseStationData,
    assessmentClassId,
    assessmentSectionId
  );

  const isQuestionSessionReady =
    expectedQuestionSessionId == null ||
    isQuestionSessionMatched(
      expectedQuestionSessionId,
      questionSessionData,
      caBaseStationData
    );

  // While question-session API is loading, keep indicator in loading state.
  // After API completes, surface disconnect + mismatch reasons.
  const disconnectReason = isQuestionSessionLoading
    ? undefined
    : getClickerDisconnectReason({
        baseStation: caBaseStationData,
        classId: assessmentClassId,
        sectionId: assessmentSectionId,
        expectedQuestionSessionId,
        firestoreQuestionSession: questionSessionData,
        checkQuestionSession: expectedQuestionSessionId != null,
      });

  const isEffectivelyOnline =
    isBaseOnline && (isQuestionSessionLoading || isQuestionSessionReady);

  const getOnlineKeyPadsCount = () => {
    // Count against the class roster, not the raw keyPads list — the Firestore
    // doc can hold stale entries from old mappings which inflate the counts
    const keyPads = caBaseStationData?.keyPads || [];
    const students = student_list || [];
    const onlineKeyPadsCount = students.filter((student) =>
      keyPads.some(
        (keypad) =>
          keypad.keypadSn === student.clickerSerialNo && keypad.isOnline
      )
    ).length;
    return { onlineKeyPadsCount, totalKeyPadsCount: students.length };
  };

  const studentList = student_list?.map((student) => {
    const response = questionSessionData?.responses.find(
      (e) => e.keypadSn === student.clickerSerialNo
    );
    return {
      ...student,
      response: response, // Add the response data to each student object
    };
  });

  const totalResponseTime =
    studentList?.reduce(
      (acc, student) => acc + (student.response?.timeSpent || 0),
      0
    ) || 0;
  const totalAnswered =
    studentList?.filter((student) => student.response).length || 0;

  // Assigned students = count from the assessment content (fetchAssessmentContent),
  // not just the roster that's joined/answered — average score is diluted by non-responders.
  const totalAssignedStudents =
    assessment_content?.assessment?.assignedStudentsCount ||
    student_list?.length ||
    0;

  const averageTime = totalAnswered
    ? Math.round((60 * totalResponseTime) / totalAnswered)
    : 0;

  const maxMarks = activeQuestion?.question
    ? activeQuestion?.question.model.value?.commonQuestionContent?.marks.reduce(
        (acc, curr) => acc + curr,
        0
      ) || 0
    : 0;
  const totalCorrectAnswersForMcqM =
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? studentList?.filter((student) => {
          const response = student.response?.response;
          return (
            response &&
            calculateMarksMCQM(activeQuestion, {
              answer: response.split(''),
            }) === maxMarks
          );
        }).length
      : undefined;

  const correctAnswer =
    activeQuestion?.question?.model.case === 'tfContentModel' ||
    activeQuestion?.question?.model.case === 'mcqSingleContentModel' ||
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? activeQuestion.question.model.value.correct
      : undefined;

  const totalCorrectAnswers =
    studentList?.filter(
      (student) =>
        student.response?.response &&
        convertAnswerOptionstoNumbers(student.response?.response) ===
          correctAnswer
    ).length || 0;

  const totalMarksObtainedForMCQM =
    activeQuestion?.question?.model.case === 'mcqMultipleContentModel'
      ? studentList?.reduce((acc, curr) => {
          const response = curr.response?.response;
          if (response) {
            const marks =
              calculateMarksMCQM(activeQuestion, {
                answer: response.split(''),
              }) ?? 0;
            return acc + marks;
          }
          return acc;
        }, 0)
      : undefined;

  const averageMarksforMcqM =
    totalMarksObtainedForMCQM && totalAssignedStudents
      ? (totalMarksObtainedForMCQM / totalAssignedStudents).toFixed(2)
      : 0;

  const percentageMarksForMcqM =
    totalMarksObtainedForMCQM && totalAssignedStudents && maxMarks
      ? Math.round(
          (totalMarksObtainedForMCQM / (totalAssignedStudents * maxMarks)) * 100
        )
      : 0;

  const averageMarks = totalAssignedStudents
    ? ((totalCorrectAnswers / totalAssignedStudents) * maxMarks).toFixed(2)
    : 0;

  const percentageMarks = totalAssignedStudents
    ? Math.round((totalCorrectAnswers / totalAssignedStudents) * 100)
    : 0;

  const getCorrectCount = () => {
    const { onlineKeyPadsCount } = getOnlineKeyPadsCount();
    const correctCount =
      totalCorrectAnswersForMcqM !== undefined
        ? totalCorrectAnswersForMcqM
        : totalCorrectAnswers;

    // If everyone answered, always show the correct count even when responses are hidden
    if (totalAnswered === onlineKeyPadsCount) return correctCount;

    // If responses are allowed to be shown, return the count even if not everyone answered
    if (showResponse === true) return correctCount;

    // Otherwise keep it hidden
    return undefined;
  };

  return (
    <ConnectedClassIndicator
      variant={isMobile ? 'connectedmobile2' : 'connected'}
      fullwidth
      isLoading={!!isQuestionSessionLoading}
      isOnline={isEffectivelyOnline}
      disconnectReason={disconnectReason}
      totalJoined={getOnlineKeyPadsCount().onlineKeyPadsCount}
      totalStudents={getOnlineKeyPadsCount().totalKeyPadsCount}
      totalAnswered={totalAnswered}
      // correct={2}
      correct={getCorrectCount()}
      averageTime={averageTime}
      averagemarks={
        totalMarksObtainedForMCQM !== undefined
          ? percentageMarksForMcqM
          : percentageMarks
      }
    />
  );
};
